if (!customElements.get('cp-benefits')) {
  customElements.define('cp-benefits', class extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      const on = (target, type, fn) => target.addEventListener(type, fn, { signal: this.abort.signal });
      this.track = this.querySelector('.cp-benefits__track');
      this.slides = [...this.track.children];
      this.button = this.querySelector('.cp-benefits__toggle');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.index = 0;
      this.reasons = new Set();
      this.interval = Math.max(8000, Number(this.dataset.interval) || 8000);
      if (this.slides.length < 2) return;
      this.slides.forEach((s, i) => s.setAttribute('aria-label', `${i + 1} sur ${this.slides.length}`));
      this.clone = this.slides[0].cloneNode(true);
      this.clone.dataset.clone = '';
      this.clone.removeAttribute('data-shopify-editor-block');
      this.clone.removeAttribute('data-block-id');
      this.clone.setAttribute('aria-hidden', 'true');
      this.clone.inert = true;
      this.track.append(this.clone);
      this.classList.add('is-ready');
      this.reasons.add('offscreen');
      this.syncSlides();
      const togglePause = () => {
        const paused = !this.reasons.has('user');
        this.button.setAttribute('aria-pressed', String(paused));
        this.button.textContent = paused ? 'Reprendre' : 'Mettre en pause';
        this.setReason('user', paused);
      };
      on(this.button, 'click', togglePause);
      on(this, 'keydown', e => {
        if (e.target === this && e.code === 'Space') { e.preventDefault(); togglePause(); }
      });
      on(this, 'pointerup', e => {
        if (e.pointerType === 'touch' && !e.target.closest('a,button')) togglePause();
      });
      on(this, 'pointerenter', e => { if (e.pointerType === 'mouse') this.setReason('hover', true); });
      on(this, 'pointerleave', e => { if (e.pointerType === 'mouse') this.setReason('hover', false); });
      on(this, 'focusin', e => { if (e.target !== this.button && e.target.matches(':focus-visible')) this.setReason('focus', true); });
      on(this, 'focusout', e => { if (!this.contains(e.relatedTarget) || e.relatedTarget === this.button) this.setReason('focus', false); });
      on(document, 'visibilitychange', () => this.setReason('hidden', document.hidden));
      on(this.motion, 'change', () => this.refreshMotion());
      on(this.track, 'transitionend', e => {
        if (e.target !== this.track || e.propertyName !== 'transform') return;
        this.moving = false;
        if (this.index === this.slides.length) {
          this.index = 0;
          this.jump();
        }
        this.syncSlides();
        this.schedule();
      });
      on(document, 'shopify:block:select', e => {
        const i = this.slides.findIndex(s => s.dataset.blockId === e.detail.blockId);
        if (i < 0) return;
        this.setReason('editor', true);
        this.index = i;
        this.moving = false;
        this.jump();
        this.syncSlides();
      });
      on(document, 'shopify:block:deselect', () => this.setReason('editor', false));
      this.observer = new IntersectionObserver(entries => this.setReason('offscreen', !entries[0].isIntersecting), { threshold: 0.15 });
      this.observer.observe(this);
      this.refreshMotion();
    }
    syncSlides() {
      const showAll = this.motion.matches;
      this.slides.forEach((s, i) => {
        const hidden = !showAll && i !== this.index % this.slides.length;
        s.inert = hidden;
        s.setAttribute('aria-hidden', String(hidden));
      });
    }
    jump() {
      this.track.style.transition = 'none';
      this.track.style.transform = `translateX(-${this.index * 100}%)`;
      void this.track.offsetWidth;
      this.track.style.transition = '';
    }
    refreshMotion() {
      this.setReason('reduced', this.motion.matches);
      if (this.motion.matches) { this.moving = false; this.index = 0; this.jump(); }
      this.syncSlides();
    }
    setReason(reason, value) {
      value ? this.reasons.add(reason) : this.reasons.delete(reason);
      this.track.getAnimations().forEach(a => this.reasons.size ? a.pause() : a.play());
      this.schedule();
    }
    schedule() {
      clearTimeout(this.timer);
      if (this.reasons.size || this.moving) return;
      this.timer = setTimeout(() => {
        this.moving = true;
        this.index += 1;
        this.track.style.transform = `translateX(-${this.index * 100}%)`;
      }, this.interval);
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      this.abort?.abort();
      this.observer?.disconnect();
      this.clone?.remove();
      this.classList.remove('is-ready');
    }
  });
}
